import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2, MapPin } from "lucide-react";
import useMetadata from "../hooks/useMetadata";

const filterOptions = [
  { label: "Alle", value: "alle" },
  { label: "Udgravning", value: "udgravning" },
  { label: "Fundamenter", value: "fundamenter" },
  { label: "Jordhåndtering", value: "jordhaandtering" },
  { label: "Sandpuder", value: "sandpuder" },
];

export default function Projects() {
  useMetadata(
    "Projekter",
    "Se vores seneste projekter inden for jord-, grave- og anlægsarbejde. Vi opdaterer løbende siden med billeder og beskrivelser af vores arbejde."
  );

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeFilter = searchParams.get("ydelse") || "alle";

  useEffect(() => {
    async function fetchProjects() {
      setLoading(true);
      try {
        const response = await fetch("https://api.dev.auxo.dk/public/v2/content/projekter");
        const data = await response.json();
        setProjects(data.items || []);
      } catch (error) {
        console.error("Fejl ved hentning af projekter:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchProjects();
  }, []);

  const handleFilterChange = (filterValue) => {
    if (filterValue === "alle") {
      searchParams.delete("ydelse");
    } else {
      searchParams.set("ydelse", filterValue);
    }
    setSearchParams(searchParams);
  };

  // Client-side filtering based on selected service keyword
  const filteredProjects = projects.filter((project) => {
    if (activeFilter === "alle") return true;

    // Map filter values to Danish keywords in the udfoerte_ydelser field
    const filterKeywordMap = {
      udgravning: "udgravning",
      fundamenter: "fundamenter",
      jordhaandtering: "jordhåndtering",
      sandpuder: "sandpuder",
    };

    const keyword = filterKeywordMap[activeFilter];
    return project.udfoerte_ydelser.da.toLowerCase().includes(keyword);
  });

  return (
    <div className="bg-white text-brand-charcoal min-h-[80vh]">
      {/* Hero Section */}
      <section className="relative py-16 sm:py-20 border-b border-gray-100 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl md:text-5xl uppercase text-brand-charcoal">
            Udførte projekter
          </h1>
          <p className="mt-4 max-w-2xl mx-auto text-sm sm:text-base text-gray-600">
            Udforsk et udvalg af vores professionelle jord-, grave- og anlægsarbejde udført for private og erhvervskunder i en 30 km radius fra Mørkøv.
          </p>
        </div>
      </section>

      {/* Projects Grid & Filter Section */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Filter Bar */}
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-12">
            {filterOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleFilterChange(option.value)}
                className={`rounded-none px-5 py-2.5 text-xs font-bold uppercase tracking-wider transition-all focus:outline-none ${
                  activeFilter === option.value
                    ? "bg-brand-accent text-white"
                    : "bg-gray-100 border border-gray-200 text-gray-600 hover:border-gray-300 hover:text-brand-charcoal"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex min-h-[40vh] items-center justify-center">
              <div className="text-center space-y-3">
                <Loader2 className="mx-auto h-8 w-8 animate-spin text-brand-accent" />
                <p className="text-xs text-gray-500">Henter projekter...</p>
              </div>
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-20 px-4 rounded-none border border-dashed border-gray-200 bg-gray-50 max-w-2xl mx-auto">
              <i className="fa-regular fa-helmet-safety text-4xl text-brand-accent mb-4 block"></i>
              <h3 className="text-lg font-bold uppercase tracking-wider text-brand-charcoal mb-2">Projekter på vej</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Vi opdaterer løbende siden med billeder og beskrivelser af vores seneste jord-, grave- og anlægsarbejde. Kontakt os gerne, hvis du ønsker referencer på lignende opgaver.
              </p>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-16 rounded-none border border-dashed border-gray-200 bg-gray-50">
              <p className="text-gray-500 text-sm">Der blev ikke fundet nogen projekter under denne kategori.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {filteredProjects.map((project) => (
                <Link
                  key={project.id}
                  to={`/projekter/${project.projekt_slug}`}
                  className="group flex flex-col overflow-hidden rounded-none border border-gray-200 bg-gray-50 transition-all hover:border-brand-accent/50 hover:bg-white hover:shadow-lg hover:translate-y-[-2px]"
                >
                  {/* Image */}
                  <div className="relative h-56 overflow-hidden bg-gray-200">
                    <img
                      src={`https://assets.auxodev.dk/cdn-cgi/image/width=500,height=350,fit=cover,quality=80,format=auto/${project.galleri[0].replace(
                        "https://assets.auxodev.dk/",
                        ""
                      )}`}
                      alt={project.projekttitel.da}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>

                  {/* Content */}
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="text-lg font-bold text-brand-charcoal group-hover:text-brand-accent transition-colors line-clamp-1 uppercase">
                      {project.projekttitel.da}
                    </h3>
                    <p className="mt-2 text-xs text-gray-500 uppercase tracking-wider font-semibold">
                      Ydelser: {project.udfoerte_ydelser.da}
                    </p>
                    <div className="mt-5 flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-brand-accent mt-auto pt-4 border-t border-gray-200">
                      Se projektet
                      <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
